import PropTypes from 'prop-types';
import { paymentInstrumentConfigs } from './ccui-props';

export const i18n = PropTypes.shape({
  formatDate: PropTypes.func.isRequired,
  formatPercent: PropTypes.func.isRequired,
  getCurrencySymbol: PropTypes.func.isRequired,
  getString: PropTypes.func.isRequired,
  isReady: PropTypes.func.isRequired,
  parseDate: PropTypes.func.isRequired,
  parseDecimal: PropTypes.func.isRequired,
});

export const legacyI18n = PropTypes.shape({
  formatDate: PropTypes.func.isRequired,
  formatPercent: PropTypes.func.isRequired,
  formatTime: PropTypes.func.isRequired,
  getCurrencySymbol: PropTypes.func.isRequired,
  getString: PropTypes.func.isRequired,
  isReady: PropTypes.func.isRequired,
  parseDate: PropTypes.func.isRequired,
  parseDecimal: PropTypes.func.isRequired,
});

export const instrumentationSchema = PropTypes.shape({
  scenario: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
    activity: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    })).isRequired,
  })).isRequired,
});

export const instumentationServiceContext = PropTypes.shape({
  logger: PropTypes.any.isRequired,
  instrumentation: PropTypes.any.isRequired,
});

export const instrumentationScenario = PropTypes.shape({
  create: PropTypes.func.isRequired,
});

export const instrumentationActivity = PropTypes.shape({
  trace: PropTypes.func.isRequired,
  error: PropTypes.func.isRequired,
});

export const permissions = PropTypes.objectOf(PropTypes.bool);

export const legacyServerContext = PropTypes.shape({
  // Required
  CurrentLocale: PropTypes.string.isRequired,
  PageTrackingId: PropTypes.string.isRequired,
  ServicesEndpoints: PropTypes.shape({
    AdInsightAPI: PropTypes.string,
    CampaignAPI: PropTypes.string.isRequired,
  }).isRequired,
  CampaignAPIPath: PropTypes.string.isRequired,

  // Not Required (e.g. not logged in yet)
  CurrentUser: PropTypes.shape({
    UserId: PropTypes.number,
    UserName: PropTypes.string,
    Email: PropTypes.string,
    Lcid: PropTypes.number,
    CustomerMarket: PropTypes.string,
  }),
  CurrentCustomer: PropTypes.shape({
    CustomerId: PropTypes.number.isRequired,
    CustomerNumber: PropTypes.string.isRequired,
    CustomerName: PropTypes.string.isRequired,
  }),
  CurrentAccount: PropTypes.shape({
    AccountId: PropTypes.number.isRequired,
    AccountNumber: PropTypes.string.isRequired,
    AdvertiserCustomerId: PropTypes.number,
    AccountMode: PropTypes.number,
    LifecycleStatus: PropTypes.number,
    CurrencyType: PropTypes.string,
    CountryCode: PropTypes.string,
  }),
  Token: PropTypes.string,
  CCBaseUrl: PropTypes.string.isRequired,
  PidlConfiguration: PropTypes.shape({
    PidlEnvironment: PropTypes.string.isRequired,
    PidlEnabledCountries: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    ExcludedCountriesByCardType: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    ThreatMetrixUrl: PropTypes.string.isRequired,
    MsaDelegateAuthUrl: PropTypes.string.isRequired,
    PidlErrorFormattingStrings: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    PidlSupportedCultureMapping: PropTypes.objectOf(PropTypes.string).isRequired,
    FriendlySepaMaxNameLength: PropTypes.number.isRequired,
  }).isRequired,
  CCUIPilots: PropTypes.objectOf(PropTypes.bool).isRequired,
  PaymentInstrumentConfigs: paymentInstrumentConfigs.isRequired,
  AuthType: PropTypes.string.isRequired,
  CookieDomain: PropTypes.string.isRequired,
  CookiePath: PropTypes.string.isRequired,
});

export const serverContext = PropTypes.shape({
  CurrentAccount: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    AccountNumber: PropTypes.string.isRequired,
    AdvertiserCustomerID: PropTypes.number,
    CurrencyCode: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
    Number: PropTypes.string.isRequired,
    Status: PropTypes.number,
  }),
  CurrentCustomer: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  }),
  CurrentLocale: PropTypes.string.isRequired,
  CurrentUser: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Email: PropTypes.string.isRequired,
    Market: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  }),
  PageTrackingId: PropTypes.string.isRequired,
  Token: PropTypes.string,
  UserPreferences: PropTypes.shape({
    AccountLevelPreferences: PropTypes.string,
    UserLevelPreferences: PropTypes.string,
  }).isRequired,
  McaServiceRoot: PropTypes.string.isRequired,
  Permissions: permissions,
});

export const domChildren = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.node),
  PropTypes.node,
]);

export const urls = PropTypes.shape({
  AccountsBillingUrl: PropTypes.string.isRequired,
  CCBaseUrl: PropTypes.string.isRequired,
  CentralizedSettingsUrl: PropTypes.string.isRequired,
  NonAzureDomain: PropTypes.string.isRequired,
  SignOutUrl: PropTypes.string.isRequired,
  SupportUrl: PropTypes.string.isRequired,
  UcmSiteUrl: PropTypes.string.isRequired,
});

export const preferenceService = PropTypes.shape({
  del: PropTypes.func.isRequired,
  find: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  set: PropTypes.func.isRequired,
  setAtUserLevel: PropTypes.func.isRequired,
});

export const legacyPageState = PropTypes.shape({
  set: PropTypes.func.isRequired,
  getPromise: PropTypes.func.isRequired,
});

export const reactRouterLocation = PropTypes.shape({
  pathname: PropTypes.string.isRequired,
  search: PropTypes.string,
  hash: PropTypes.string,
});

export const reactRouterMatch = PropTypes.shape({
  params: PropTypes.objectOf(PropTypes.oneOf([
    PropTypes.string,
    PropTypes.number,
  ])).isRequired,
  path: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  isExact: PropTypes.bool.isRequired,
});

export const reactRouterHistory = PropTypes.shape({
  go: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
});

export const navigationItems = PropTypes.arrayOf(PropTypes.shape({
  name: PropTypes.string.isRequired,
  route: PropTypes.string.isRequired,
}));

export const parentActivityType = PropTypes.shape({
  ActivityType: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 41, 42, 43]).isRequired,
  Id: PropTypes.string.isRequired,
  Message: PropTypes.string.isRequired,
  Likes: PropTypes.number.isRequired,
  Replies: PropTypes.number.isRequired,
  Shares: PropTypes.number,
  Profile: PropTypes.shape({
    Id: PropTypes.string.isRequired,
    // Internal = 1, Facebook = 2, Instagram = 3,
    ChannelType: PropTypes.oneOf([1, 2, 3]).isRequired,
    ChannelName: PropTypes.string.isRequired,
  }),
  From: PropTypes.shape({
    // FacebookUser = 1, FacebookPage = 2, InstagramUser = 3,
    ChannelUserType: 2,
    Id: PropTypes.string,
    Name: PropTypes.string,
    Profile: null,
  }),
  CreatedTime: PropTypes.string,
  IsCompleted: false,
});

export const activityItemType = PropTypes.shape({
  // FacebookWallPost = 1, FacebookComment = 2, FacebookAdComment = 3,
  // FacebookReview = 4, FacebookPrivateMessage = 5, FacebookPost = 6,
  // InstagramComment = 41, InstagramAdComment = 42, InstagramPost = 43
  ActivityType: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 41, 42, 43]).isRequired,
  Id: PropTypes.string.isRequired,
  Message: PropTypes.string,
  MediaList: PropTypes.arrayOf(PropTypes.shape({
    SourceUrl: PropTypes.string,
    Target: PropTypes.string,
    PreviewUrl: PropTypes.string,
    Title: PropTypes.string,
    Type: PropTypes.oneOf([1, 2, 3, 4]), // Image = 1, Gif = 2, Video = 3, Share = 4,
  })),
  ParentActivity: parentActivityType,
  CanComment: PropTypes.bool.isRequired,
  CanHide: PropTypes.bool.isRequired,
  CanLike: PropTypes.bool.isRequired,
  UserLikes: PropTypes.bool.isRequired,
  IsHidden: PropTypes.bool.isRequired,
  Profile: PropTypes.shape({
    Id: PropTypes.string.isRequired,
    // Internal = 1, Facebook = 2, Instagram = 3,
    ChannelType: PropTypes.oneOf([1, 2, 3]).isRequired,
    ChannelName: PropTypes.string.isRequired,
  }),
  CreatedTime: PropTypes.string.isRequired,
  From: PropTypes.shape({
    // FacebookUser = 1, FacebookPage = 2, InstagramUser = 3,
    ChannelUserType: PropTypes.oneOf([1, 2, 3]).isRequired,
    Url: PropTypes.string,
    IsBlocked: PropTypes.bool,
    Id: PropTypes.string,
    Name: PropTypes.string,
    ProfileImageUrl: PropTypes.string,
  }).isRequired,
  // Active = 1, Deleted = 2,
  Status: PropTypes.oneOf([1, 2]).isRequired,
  Url: PropTypes.string,
  IsCompleted: PropTypes.bool.isRequired,
});

export const profileShape = PropTypes.shape({
  Id: PropTypes.string.isRequired,
  ChannelType: PropTypes.string.isRequired,
  ChannelId: PropTypes.string.isRequired,
  ChannelAccountId: PropTypes.string.isRequired,
  ChannelName: PropTypes.string.isRequired,
  ProfileImageUrl: PropTypes.string.isRequired,
  AuthorizationData: PropTypes.shape({
    AccessToken: PropTypes.string,
  }),
  Status: PropTypes.string.isRequired,
});

export const campaignCategoriesShape = PropTypes.arrayOf(PropTypes.shape({
  Id: PropTypes.number.isRequired,
  CategoryName: PropTypes.string.isRequired,
}));

export const socialProfileShape = PropTypes.shape({
  Id: PropTypes.string,
  ChannelType: PropTypes.string,
  ChannelId: PropTypes.string,
  ChannelAccountId: PropTypes.string,
  ChannelName: PropTypes.string,
  ProfileImageUrl: PropTypes.string,
  SkipOfflineProfileInitTaskCreation: PropTypes.bool,
  Status: PropTypes.string,
  AccountId: PropTypes.number,
  CustomerId: PropTypes.number,
});

export const campaignInfoShape = PropTypes.shape({
  Id: PropTypes.string.isRequired,
  Name: PropTypes.string,
  Goal: PropTypes.string.isRequired,
  SocialProfiles: PropTypes.arrayOf(socialProfileShape),
  Categories: campaignCategoriesShape,
  McaBusinessListing: PropTypes.shape({
    BusinessName: PropTypes.string.isRequired,
    BusinessAddress: PropTypes.shape({
      StreetAddress: PropTypes.string.isRequired,
      StreetAddress2: PropTypes.string,
      CityName: PropTypes.string.isRequired,
      ProvinceName: PropTypes.string.isRequired,
      PostalCode: PropTypes.string.isRequired,
    }).IsRequired,
    PhoneNumber: PropTypes.string.isRequired,
  }).isRequired,
  Website: PropTypes.string.isRequired,
  Status: PropTypes.string.isRequired,
  InternalInfo: PropTypes.shape({
    SourceId: PropTypes.number,
    ModifiedByUserId: PropTypes.number,
    CreatedDTim: PropTypes.string.isRequired,
    ModifiedDTime: PropTypes.string,
  }).isRequired,
});

export const tileFooterConfigShape = PropTypes.shape({
  isRequired: PropTypes.bool,
  buttons: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    callback: PropTypes.func,
  })),
});

export const tileHeaderConfigShape = PropTypes.shape({
  isRequired: PropTypes.bool,
  isLargeHeader: PropTypes.bool,
  renderHeaderText: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    callback: PropTypes.func,
  })),
  i18n: i18n.isRequired,
});

export const tileContentConfigShape = PropTypes.shape({
  styles: PropTypes.shape({
    root: PropTypes.shape({
      width: PropTypes.string,
      height: PropTypes.string,
    }),
  }),
  shimmerLineCount: PropTypes.number,
  onRenderShimmer: PropTypes.func,
  isShimmerRequired: PropTypes.bool,
});

export const adsPerformanceMetricsShape = PropTypes.shape({
  LocationId: PropTypes.number.isRequired,
  MetricDate: PropTypes.string.isRequired,
  Impressions: PropTypes.number.isRequired,
  Clicks: PropTypes.number.isRequired,
  Spend: PropTypes.number.isRequired,
  Conversions: PropTypes.number.isRequired,
  Calls: PropTypes.number.isRequired,
  Directions: PropTypes.number.isRequired,
  Reactions: PropTypes.number.isRequired,
  Comments: PropTypes.number.isRequired,
  Shares: PropTypes.number.isRequired,
  Reach: PropTypes.number.isRequired,
});

export const adsPerformanceTopMetricsShape = PropTypes.shape({
  Impressions: PropTypes.number.isRequired,
  Clicks: PropTypes.number.isRequired,
  Spend: PropTypes.number.isRequired,
  Conversions: PropTypes.number.isRequired,
  Calls: PropTypes.number.isRequired,
  Directions: PropTypes.number.isRequired,
  Reactions: PropTypes.number.isRequired,
  Comments: PropTypes.number.isRequired,
  Shares: PropTypes.number.isRequired,
});

export const mcaLineChartConfigShape = PropTypes.shape({
  sameYAxis: PropTypes.bool,
  granularity: PropTypes.number.isRequired,
  tooltipTitle: PropTypes.string,
  metrics: PropTypes.arrayOf(PropTypes.shape({
    legend: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.number).isRequired,
    startDate: PropTypes.number.isRequired,
  })).isRequired,
});

export const budgetProp = PropTypes.shape({
  DailyBudget: PropTypes.number,
  MinimumEstimatedClicks: PropTypes.number,
  MinimumEstimatedImpressions: PropTypes.number,
  MaximumEstimatedClicks: PropTypes.number,
  MaximumEstimatedImpressions: PropTypes.number,
  MonthlyBudget: PropTypes.number,
});

export const budgetSuggestion = PropTypes.shape({
  MinimumBudget: budgetProp,
  CompetitorBudgetLowerBound: budgetProp,
  SuggestedBudget: budgetProp,
  CompetitorBudgetUpperBound: budgetProp,
  MaximumBudget: budgetProp,
  GlobalMaximumBudget: budgetProp,
});

export const searchAdDataShape = PropTypes.shape({
  titlePart1: PropTypes.string,
  titlePart2: PropTypes.string,
  titlePart3: PropTypes.string,
  text: PropTypes.string,
  textPart2: PropTypes.string,
  urlPrefix: PropTypes.string,
  destinationUrlWithoutPrefix: PropTypes.string,
});

export const mediaAdDataShape = PropTypes.shape({
  title: PropTypes.string,
  description: PropTypes.string,
  urlPrefix: PropTypes.string,
  destinationUrlWithoutPrefix: PropTypes.string,
});

export const fbAdAccountShape = PropTypes.shape({
  Id: PropTypes.string.isRequired,
  ChannelTypeId: PropTypes.string.isRequired,
  ChannelData: PropTypes.shape({
    ExternalAdAccountId: PropTypes.string.isRequired,
    Status: PropTypes.string.isRequired,
  }).isRequired,
});

export const keywordSuggestions = PropTypes.arrayOf(PropTypes.shape({
  IsSelected: PropTypes.bool,
  IsSuggested: PropTypes.bool,
  KeywordText: PropTypes.string.isRequired,
  SourceId: PropTypes.string,
}));

export const keyword = PropTypes.arrayOf(PropTypes.shape({
  Id: PropTypes.string.isRequired,
  KeywordText: PropTypes.string.isRequired,
  SourceId: PropTypes.string,
  IsSelected: PropTypes.bool,
  IsSuggested: PropTypes.bool,
  Bid: PropTypes.number,
  MatchType: PropTypes.string,
  McaCampaignId: PropTypes.number.isRequired,
  ClientId: PropTypes.string.isRequired,
  KeywordType: PropTypes.string.isRequired,
  Status: PropTypes.string.isRequired,
  EditorialStatus: PropTypes.string.isRequired,
  CreateDateTime: PropTypes.string.isRequired,
  EntitySourceType: PropTypes.string,
  ActionType: null,
  EditorialInfos: PropTypes.array,
}));

export const panelActionMarker = PropTypes.shape({
  openPanel: PropTypes.func.isRequired,
  startLoading: PropTypes.func.isRequired,
  stopLoading: PropTypes.func.isRequired,
  onLoadedRecommendation: PropTypes.func.isRequired,
  markToSaveData: PropTypes.func.isRequired,
  markToDeleteData: PropTypes.func.isRequired,
  startSaving: PropTypes.func.isRequired,
  stopSaving: PropTypes.func.isRequired,
  closePanel: PropTypes.func.isRequired,
});
